import { API, Auth } from 'aws-amplify';
import { logger } from '../utils/logger';

export class Api {
  apiName: string;
  constructor(apiName: string) {
    this.apiName = apiName;
  }

  /**
   * Sends a POST request to create or submit data to the specified API endpoint.
   * 
   * @param path - API endpoint path relative to the base API URL (e.g., '/workflow', '/space')
   * @param payload - Optional request body containing the data to send. Can be any serializable object
   * 
   * @returns Promise<any> - The response data from the API
   * 
   * @throws {Error} - Throws an error if the request fails due to network issues, authentication, or server errors
   * 
   * @example
   * ```typescript
   * // Create a new MFR workflow
   * const mfrWorkflow = {
   *   workflow: {
   *     name: "Q3 2025 Amazon Devices MFR Report",
   *     description: "MFR analysis for Amazon Devices across EU5 markets",
   *     spaceId: "550e8400-e29b-41d4-a716-446655440000",
   *     inputs: {
   *       productLine: "amazon-devices",
   *       marketplace: ["de", "fr", "it"],
   *       currency: "usd-constant",
   *       period: "q3-2025"
   *     },
   *     steps: [
   *       {
   *         id: "550e8400-e29b-41d4-a716-446655440001",
   *         inputs: {
   *           include_table_generation: true,
   *           include_commentary_generation: false,
   *           currency: "millions"
   *         }
   *       }
   *     ],
   *     tags: ["MFR"],
   *     isTemplate: false
   *   }
   * };
   * const result = await api.post('/workflow', mfrWorkflow);
   * ```
   */
  async post(path: string, payload?: any) {
    const requestOptions: any = {
      ...(payload && { body: payload }),
      headers: {
        Authorization: `${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
      },
    };

    try {
      const response = await API.post(this.apiName, path, requestOptions);
      // TODO: Remove this before launch
      logger.info('API POST Success:', response);
      return response;
    } catch (error: any) {
      // TODO: Remove this before launch
      logger.error('API POST Error:', {
        message: error.message,
        status: error.status || error.statusCode,
        path: path
      });
      throw error;
    }
  }

  /**
   * Sends a GET request to retrieve data from the specified API endpoint.
   * 
   * @param path - API endpoint path relative to the base API URL (e.g., '/workflow', '/space/{spaceId}')
   * 
   * @returns Promise<any> - The response data from the API
   * 
   * @throws {Error} - Throws an error if the request fails due to network issues, authentication, or server errors
   * 
   * @example
   * ```typescript
   * // Get all workflows for a space
   * const workflows = await api.get('/space/550e8400-e29b-41d4-a716-446655440000/workflow');
   * 
   * // Get a specific workflow
   * const workflow = await api.get('/space/550e8400-e29b-41d4-a716-446655440000/workflow/550e8400-e29b-41d4-a716-446655440001');
   * ```
   */
  async get(path: string) {
    const requestOptions: any = {
      headers: {
        Authorization: `${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
      },
    };

    try {
      const response = await API.get(this.apiName, path, requestOptions);
      // TODO: Remove this before launch
      logger.info('API GET Success:', response);
      return response;
    } catch (error: any) {
      // TODO: Remove this before launch
      logger.error('API GET Error:', {
        message: error.message,
        status: error.status || error.statusCode,
        path: path
      });
      throw error;
    }
  }

  /**
   * Sends a PUT request to update data at the specified API endpoint.
   * 
   * @param path - API endpoint path relative to the base API URL (e.g., '/capability/{capabilityId}')
   * @param payload - Request body containing the data to update. Can be any serializable object
   * 
   * @returns Promise<any> - The response data from the API
   * 
   * @throws {Error} - Throws an error if the request fails due to network issues, authentication, or server errors
   */
  async put(path: string, payload: any) {
    const requestOptions: any = {
      body: payload,
      headers: {
        Authorization: `${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
      },
    };

    try {
      const response = await API.put(this.apiName, path, requestOptions);
      // TODO: Remove this before launch
      logger.info('API PUT Success:', response);
      return response;
    } catch (error: any) {
      // TODO: Remove this before launch
      logger.error('API PUT Error:', {
        message: error.message,
        status: error.status || error.statusCode,
        path: path
      });
      throw error;
    }
  }

  /**
   * Sends a DELETE request to remove data at the specified API endpoint.
   * 
   * @param path - API endpoint path relative to the base API URL (e.g., '/workflow/{workflowId}')
   * 
   * @returns Promise<any> - The response data from the API
   * 
   * @throws {Error} - Throws an error if the request fails due to network issues, authentication, or server errors
   */
  async delete(path: string) : Promise<void> {
    const requestOptions: any = {
      headers: {
        Authorization: `${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
      },
    };

    try {
      const response = await API.del(this.apiName, path, requestOptions);
      // TODO: Remove this before launch
      logger.info('API DELETE Success:', response);
    } catch (error: any) {
      // TODO: Remove this before launch
      logger.error('API DELETE Error:', {
        message: error.message,
        status: error.status || error.statusCode,
        path: path
      });
      throw error;
    }
  }
}
